import { Button, Flex, Text, TextInput, Title } from "@mantine/core";
import { FormattedMessage } from "react-intl";
import { usePOAStepsStore } from "../../../hooks/ui/poaStepsDataStore";
import { FieldMeta, useForm } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { useUpdateCustomerData } from "../../../hooks/ui/";
import { FooterButtons } from "../../footerButtons";
import {
  APP_POA_STEP,
  REPLAY_BLOCK_CLASS,
  trackEvent,
} from "../../../utils/statsig";
import {
  ContactDetails,
  contactDetailsSchema,
  normalizeToE164,
} from "../../../utils/contactDetailsValidation";

function getError(meta: FieldMeta) {
  return meta.isTouched ? meta.errors?.[0] : undefined;
}

export default function AddContactDetails({ onBack }: { onBack: () => void }) {
  const { nextStep, app } = usePOAStepsStore();
  const { updateEmail, updatePhoneNumber, customerData } =
    useUpdateCustomerData();

  const { Field, handleSubmit, Subscribe } = useForm({
    defaultValues: {
      email: customerData.email ?? "",
      phoneNumber: customerData.phoneNumber ?? "",
    } as ContactDetails,
    onSubmit: async ({ value }) => {
      updateEmail(value.email);
      updatePhoneNumber(normalizeToE164(value.phoneNumber));
      if (app) {
        trackEvent("pension_app_poa_next_clicked", {
          step: APP_POA_STEP.contactDetails,
        });
      }
      nextStep();
    },
    validatorAdapter: zodValidator(),
    validators: {
      onChange: contactDetailsSchema,
    },
  });

  return (
    <Flex justify="center" align="center" data-testid="poa-contact-details">
      <Flex direction="column" maw={672} w={"100%"}>
        <Title order={2} mb="1rem" ff={"var(--wallet-font-family-sambla-sans)"}>
          {app ? (
            <FormattedMessage
              id="app.poaOnb.contact.headline"
              defaultMessage="Bekräfta dina kontaktuppgifter"
            />
          ) : (
            "Hur kan vi kontakta dig?"
          )}
        </Title>
        <Text mb={"xl"}>
          {app ? (
            <FormattedMessage
              id="app.poaOnb.contact.description"
              defaultMessage="Kontaktuppgifterna används i tillståndet du ger via Fullmaktskollen, så att vi kan hämta dina pensionsuppgifter."
            />
          ) : (
            "Vi använder email för att skicka dig information om din pension och ringer upp dig när det är dags för rådgivning. Uppgifterna kommer aldrig att delas med någon annan och används inte i marknadsföringssyfte."
          )}
        </Text>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit();
          }}
        >
          <Field name="email">
            {({ state, handleChange, handleBlur }) => (
              <TextInput
                placeholder="mail@mail.com"
                label="Email"
                // Block the value, not the label.
                classNames={{ input: REPLAY_BLOCK_CLASS }}
                withAsterisk={!app}
                mb={"lg"}
                value={state.value}
                onBlur={(e) => {
                  handleBlur();
                  updateEmail(e.target.value);
                }}
                onChange={(e) => handleChange(e.target.value)}
                type="email"
                autoComplete="email"
                aria-required="true"
                error={getError(state.meta)}
              />
            )}
          </Field>

          <Field name="phoneNumber">
            {({ state, handleChange, handleBlur }) => (
              <TextInput
                placeholder="07XXXXXXXX"
                label={app ? "Telefonnummer" : "Telefon"}
                classNames={{ input: REPLAY_BLOCK_CLASS }}
                withAsterisk={!app}
                mb={"lg"}
                value={state.value}
                onBlur={(e) => {
                  handleBlur();
                  updatePhoneNumber(e.target.value);
                }}
                onChange={(e) => handleChange(e.target.value)}
                type={"tel"}
                autoComplete="tel"
                aria-required="true"
                error={getError(state.meta)}
              />
            )}
          </Field>

          <Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <FooterButtons app={app}>
                <Button
                  type="button"
                  variant="default"
                  onClick={() => {
                    if (app) {
                      trackEvent("pension_app_poa_back_clicked", {
                        step: APP_POA_STEP.contactDetails,
                      });
                    }
                    onBack();
                  }}
                  data-testid="back-button"
                >
                  <FormattedMessage
                    id="bottomNav.back"
                    defaultMessage="Tillbaka"
                  />
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmit}
                  loading={isSubmitting}
                  onClick={() => handleSubmit()}
                  data-testid="next"
                >
                  <FormattedMessage
                    id="bottomNav.next"
                    defaultMessage="Nästa"
                  />
                </Button>
              </FooterButtons>
            )}
          </Subscribe>
        </form>
      </Flex>
    </Flex>
  );
}
